import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useUser } from '../store/userState'
import useUserStore from '../utils/store/users'
import {
  BuildingOffice2Icon,
  CubeIcon,
  CurrencyDollarIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/solid'
import { Avatar, Tooltip } from '@material-tailwind/react'
import rabbit from '../assets/avatars/rabbit.png'

const deptIcon = (name) => {
  if (name === 'Warehouse') return TruckIcon
  if (name === 'Pricing') return CurrencyDollarIcon
  if (name === 'Supplier Management') return CubeIcon
  if (name?.toLowerCase().includes('listing')) return BuildingOffice2Icon
  return WrenchScrewdriverIcon
}

const DepartmentCards = ({ department, deptId, deptTools, index }) => {
  const [isLoad, setIsLoad] = useState(false)
  const { activeUsers, fetchActiveUsers } = useUserStore()

  useEffect(() => {
    setIsLoad(true)
    fetchActiveUsers().finally(() => setIsLoad(false))
  }, [fetchActiveUsers])

  const sortedUsers = [...activeUsers].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  )

  const { user } = useUser()
  const adminPermission = user.permissions[0].department
  const departmentPermission = user.permissions[0].departmentId

  let permission
  if (adminPermission === 'Admin Access') {
    permission = false
  } else if (deptId !== departmentPermission) {
    permission = true
  } else {
    permission = null
  }

  const maxAvatars = 9
  const tools = deptTools[index] || []
  const visibleTools = tools.slice(0, 3)
  const excessToolsCount = tools.length - 3
  const Icon = deptIcon(department)
  const deptUsers = sortedUsers.filter(
    (u) => u.permissions?.[0]?.departmentId === deptId
  )
  const route =
    deptId === '6622a8f097939ed77a51d053'
      ? '/listings-tools'
      : department === 'Warehouse'
        ? '/warehouse-tools'
        : department === 'Pricing'
          ? '/pricing-tools'
          : department === 'Supplier Management'
            ? '/sm-tools'
            : '/workspace-empty'

  return (
    <article
      className={`nx-panel flex h-full flex-col gap-4 p-5 ${
        permission ? 'opacity-60' : 'hover:border-signal/40'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-rule bg-surface text-signal">
          <Icon className="h-5 w-5" aria-hidden />
        </div>
        <span className={permission ? 'nx-chip-bad' : 'nx-chip-ok'}>
          {permission ? 'No access' : 'Ready'}
        </span>
      </div>

      <div>
        <h2 className="font-display text-2xl tracking-wide text-paper">{department}</h2>
        <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.16em] text-muted">
          Tools · {tools.length}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {visibleTools.map(({ toolName }) => (
          <span key={toolName} className="nx-chip-idle">
            <WrenchScrewdriverIcon className="h-3 w-3" />
            {toolName}
          </span>
        ))}
        {excessToolsCount > 0 && (
          <span className="nx-chip-info">+{excessToolsCount}</span>
        )}
      </div>

      <div className="mt-auto flex items-center justify-between gap-3 pt-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center -space-x-3">
            {isLoad
              ? [...Array(4)].map((_, i) => (
                  <Avatar
                    key={i}
                    size="sm"
                    variant="circular"
                    alt=""
                    className="animate-pulse border-2 border-surface"
                    src={rabbit}
                  />
                ))
              : deptUsers.slice(0, maxAvatars).map(({ firstName, lastName }, i) => (
                  <Tooltip content={`${firstName} ${lastName}`} key={`${firstName}-${i}`}>
                    <Avatar
                      size="sm"
                      variant="circular"
                      alt={firstName}
                      className="border-2 border-surface"
                      src={rabbit}
                    />
                  </Tooltip>
                ))}
          </div>
          {deptUsers.length > maxAvatars && (
            <span className="text-xs text-muted">+{deptUsers.length - maxAvatars}</span>
          )}
        </div>

        <Link
          to={route}
          className={`nx-btn-primary text-xs ${permission ? 'pointer-events-none opacity-50' : ''}`}
          aria-disabled={!!permission}
          onClick={(e) => {
            if (permission) e.preventDefault()
          }}
        >
          {permission ? 'No access' : 'Access'}
        </Link>
      </div>
    </article>
  )
}

export default DepartmentCards
